// KiraWelcome.jsx — first-impression welcome overlay shown once per session
// after login.  Three-zone layout:
//   LEFT (fixed)   — vertical briefing rail (5 items)
//   CENTER         — avatar + K.I.R.A brand + typewriter greeting + check-in
//   RIGHT (fixed)  — unified to-do rail (chat + project tasks, owner-scoped)
//
// The avatar's mood is driven by the SERVER-provided `emotion` (Session B).
// The to-do rail is fed by GET /api/kira/todos (owner-scoped).
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowRight, Clock, Calendar, MessageSquare, Bell,
  Sun, Coffee, Moon,
  ListTodo, Sparkles, Folder, Mail, Send,
} from "lucide-react";
import KiraAvatar, { mapEmotion } from "./KiraAvatar";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

// Valid KiraAvatar emotions — a server emotion in this set passes straight
// through; anything else is routed through mapEmotion.
const AVATAR_EMOTIONS = new Set([
  "neutral", "thinking", "happy", "alert", "error",
  "loading", "listening", "celebrating", "excited",
]);
const NEON = "#00FF99";
const TYPE_MS = 30;
const FADE_MS = 600;
const AVATAR_SIZE = 120;
const BUBBLE_SIZE = 68;
const BUBBLE_MARGIN = 24;

export default function KiraWelcome({
  greeting = "",
  emotion = "",
  briefing = null,
  todos = null,
  isLoading = false,
  onDismiss,
  // Bounded check-in conversation pipeline (from useKira via KiraWelcomePage).
  onSend,
  messages = [],
  chatEmotion = "",
  chatLoading = false,
}) {
  const [typedGreeting, setTypedGreeting] = useState("");
  const [shrinkStyle,   setShrinkStyle]   = useState(null);
  const [entranceDone,  setEntranceDone]  = useState(false);
  const [showItems,     setShowItems]     = useState(false);

  // Centre check-in composer
  const [composerText, setComposerText] = useState("");

  // Right-hand to-do rail UI state
  const [todoFilter,  setTodoFilter]  = useState("all");
  const [prioritised, setPrioritised] = useState(false);

  // Check-in transcript (KIRA's inline replies) + auto-scroll to newest.
  const conversation = Array.isArray(messages) ? messages : [];
  const transcriptEndRef = useRef(null);
  useEffect(() => {
    if (transcriptEndRef.current) {
      transcriptEndRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
    }
  }, [conversation.length, chatLoading]);

  // ── Attendance state (with check-in button + live timer)
  const [checkedIn,   setCheckedIn]   = useState(!!briefing?.attendance?.checked_in);
  const [checkInTime, setCheckInTime] = useState(briefing?.attendance?.check_in_time || null);
  const [elapsed,     setElapsed]     = useState(0);
  const [checkInBusy, setCheckInBusy] = useState(false);

  const avatarRef         = useRef(null);
  const composerRef       = useRef(null);
  const dismissedRef      = useRef(false);
  const greetingLockedRef = useRef(false);

  // Keep attendance state in sync when briefing arrives after mount
  useEffect(() => {
    if (briefing?.attendance?.checked_in) {
      setCheckedIn(true);
      if (briefing.attendance.check_in_time) {
        setCheckInTime(briefing.attendance.check_in_time);
      }
    }
  }, [briefing?.attendance?.checked_in, briefing?.attendance?.check_in_time]);

  // ── Live timer: counts up every second while checked in
  useEffect(() => {
    if (!checkedIn || !checkInTime) return;
    const start = new Date(checkInTime).getTime();
    if (!Number.isFinite(start)) return;
    const initial = Math.max(0, Math.floor((Date.now() - start) / 1000));
    setElapsed(initial);
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [checkedIn, checkInTime]);

  // ── Dismiss handler (computes shrink-to-corner transform)
  // Every deliberate exit (the button and Esc) funnels through here, so the
  // diary save in onDismiss and the check-in-done signal below fire exactly
  // once, on every way out. dismissedRef makes it idempotent.
  const handleDismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    const r = avatarRef.current?.getBoundingClientRect();
    if (r) {
      const cx = r.left + r.width / 2;
      const cy = r.top  + r.height / 2;
      const tx = window.innerWidth  - BUBBLE_MARGIN - BUBBLE_SIZE / 2;
      const ty = window.innerHeight - BUBBLE_MARGIN - BUBBLE_SIZE / 2;
      const dx = tx - cx;
      const dy = ty - cy;
      const targetScale = (BUBBLE_SIZE - 12) / AVATAR_SIZE;
      setShrinkStyle({
        transform:  `translate(${dx}px, ${dy}px) scale(${targetScale})`,
        transition: `transform ${FADE_MS}ms cubic-bezier(0.4,0,0.2,1)`,
      });
    }
    setTimeout(() => {
      // onDismiss saves the check-in conversation to the diary, so signal
      // AFTER it. DiaryNudgeConsent listens for this to re-check for a
      // pending nudge offer; the retired DiaryLoginCheckin.close() used to
      // dispatch it, and this keeps that parity now the welcome page is the
      // morning check-in.
      onDismiss?.();
      try { window.dispatchEvent(new Event("diary_checkin_done")); } catch { /* ignore */ }
    }, FADE_MS);
  }, [onDismiss]);

  // ── Entrance burst: an "excited" arrival for ~1.6s, then the avatar settles
  // to the SERVER-provided emotion (Session B owns the mood). No client-side
  // tone guessing here.
  useEffect(() => {
    const t = setTimeout(() => setEntranceDone(true), 1600);
    return () => clearTimeout(t);
  }, []);

  // ── Greeting typewriter — locked per-string so a late API reply
  // replacing an early fallback restarts the type-out instead of being
  // suppressed by the original lock.
  useEffect(() => {
    if (!greeting) return;
    if (greetingLockedRef.current && greetingLockedRef.currentValue === greeting) return;
    greetingLockedRef.current = true;
    greetingLockedRef.currentValue = greeting;
    let i = 0;
    const id = setInterval(() => {
      i++;
      setTypedGreeting(greeting.slice(0, i));
      if (i >= greeting.length) {
        clearInterval(id);
        setShowItems(true);
      }
    }, TYPE_MS);
    return () => clearInterval(id);
  }, [greeting]);

  // ── Esc = skip, but ONLY when the check-in composer is not in use.
  // Leaving is a deliberate act: there is no idle timer, and the page never
  // navigates on its own. Esc stays because it is the only keyboard exit
  // (the composer takes Enter for send), but it is guarded on focus AND on
  // pending text, because dismissing mid-sentence would commit a truncated
  // half of a conversation as the day's morning diary entry.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const typing =
        (composerRef.current && document.activeElement === composerRef.current) ||
        composerText.trim().length > 0;
      if (typing) return;
      handleDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleDismiss, composerText]);

  // ── Check-in handler
  const handleCheckIn = async () => {
    if (checkInBusy) return;
    setCheckInBusy(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE}/api/attendance/clock`, {
        method: "POST",
        headers: {
          "Content-Type":  "application/json",
          Authorization:   `Bearer ${token}`,
        },
        body: JSON.stringify({ action: "in" }),
      });
      if (res.ok) {
        setCheckedIn(true);
        setCheckInTime(new Date().toISOString());
        setElapsed(0);
      }
    } catch {
      // Silent — user can retry
    } finally {
      setCheckInBusy(false);
    }
  };

  // ── Centre check-in composer → sends the typed text through the KIRA
  // pipeline and clears the input. It does NOT dismiss/navigate; KIRA's reply
  // renders inline in the transcript above. The user leaves ONLY by a
  // deliberate act: the "Go to dashboard" button, or Esc when they are not
  // mid-sentence. Nothing navigates on a timer.
  const submitComposer = () => {
    const text = composerText.trim();
    if (!text || chatLoading) return;
    onSend?.(text);
    setComposerText("");
  };

  // ── Avatar emotion. On arrival it's the SERVER greeting emotion (Session B).
  // Once the check-in conversation starts, the avatar reacts LIVE to the
  // exchange via kira.emotion (thinking → happy/alert/error/... per reply).
  // A valid avatar emotion passes straight through; any raw/unknown greeting
  // tag is mapped via mapEmotion. Excited during the brief entrance.
  const resolvedEmotion = emotion
    ? (AVATAR_EMOTIONS.has(emotion) ? emotion : mapEmotion(emotion, greeting))
    : null;
  const inConversation = conversation.length > 0;
  const conversationEmotion =
    chatEmotion && AVATAR_EMOTIONS.has(chatEmotion) ? chatEmotion : null;
  const shownEmotion = !entranceDone
    ? "excited"
    : inConversation
      ? (conversationEmotion || resolvedEmotion || "happy")
      : (resolvedEmotion || (greeting ? "happy" : "thinking"));

  // ── To-do rail: apply the active filter + optional prioritise sort. The
  // "Let KIRA prioritise" ordering is a CLIENT-SIDE sort for v1 (due urgency →
  // priority → ease).
  const visibleTodos = useMemo(
    () => arrangeTodos(todos || [], todoFilter, prioritised),
    [todos, todoFilter, prioritised]
  );

  const isFading = !!shrinkStyle;

  // ── Time-of-day metadata for item 1 — uses local clock.
  const hour = new Date().getHours();
  const TimeIcon =
    (hour >= 5 && hour < 12) ? Sun
      : (hour >= 12 && hour < 17) ? Coffee
      : Moon;
  const timeGreeting =
    (hour >= 5  && hour < 12) ? "Good morning"
      : (hour >= 12 && hour < 17) ? "Good afternoon"
      : (hour >= 17 && hour < 21) ? "Good evening"
      : "Good night";
  const dateLabel = new Date().toLocaleDateString("en-GB", {
    weekday: "short", day: "numeric", month: "short",
  });

  // ── Briefing items (order: date, attendance, leave, dms, notifications)
  const items = [
    {
      Icon:       TimeIcon,
      iconColor:  "rgba(255,200,0,0.85)",
      value:      dateLabel,
      label:      timeGreeting,
    },
    {
      // Attendance — special-cased renderer below
      kind:       "attendance",
    },
    {
      Icon:       Calendar,
      iconColor:  NEON,
      value:      briefing?.leaveBalance != null ? `${briefing.leaveBalance} days` : "—",
      label:      "Annual leave",
    },
    {
      Icon:       MessageSquare,
      iconColor:  briefing?.unreadDMs > 0 ? NEON : "rgba(255,255,255,0.3)",
      value:      briefing?.unreadDMs > 0 ? `${briefing.unreadDMs} messages` : "No messages",
      label:      "Unread DMs",
    },
    {
      Icon:       Bell,
      iconColor:  briefing?.unreadNotifs > 0 ? NEON : "rgba(255,255,255,0.3)",
      value:      briefing?.unreadNotifs > 0 ? `${briefing.unreadNotifs} alerts` : "All clear",
      label:      "Notifications",
    },
  ];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        background: "#050505",
        opacity: isFading ? 0 : 1,
        transition: `opacity ${FADE_MS}ms ease`,
        animation: !isFading ? "kiraWelcomeIn 400ms ease forwards" : "none",
      }}
    >
      {/* LEFT briefing rail — fixed, vertically centered */}
      {showItems && briefing && (
        <div style={{
          position:      "fixed",
          left:          32,
          top:           "50%",
          transform:     "translateY(-50%)",
          width:         260,
          display:       "flex",
          flexDirection: "column",
          gap:           16,
          zIndex:        1,
        }}>
          {items.map((it, i) => {
            const delay = `${i * 150}ms`;
            if (it.kind === "attendance") {
              return (
                <BriefingRow
                  key="attendance"
                  delay={delay}
                  Icon={Clock}
                  iconColor={checkedIn ? NEON : "rgba(255,180,0,0.85)"}
                  value={
                    checkedIn
                      ? formatTimeShort(checkInTime)
                      : "Not checked in"
                  }
                  label={checkedIn ? "Checked in" : "Attendance"}
                  right={
                    checkedIn ? (
                      <span style={{
                        color:      "rgba(0,255,153,0.7)",
                        fontSize:   12,
                        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
                        whiteSpace: "nowrap",
                      }}>
                        {formatElapsed(elapsed)}
                      </span>
                    ) : (
                      <button
                        onClick={handleCheckIn}
                        disabled={checkInBusy}
                        style={{
                          background:   "rgba(0,255,153,0.15)",
                          border:       "1px solid rgba(0,255,153,0.4)",
                          color:        NEON,
                          borderRadius: 6,
                          padding:      "4px 10px",
                          fontSize:     11,
                          fontWeight:   600,
                          cursor:       checkInBusy ? "default" : "pointer",
                          opacity:      checkInBusy ? 0.5 : 1,
                          transition:   "background 0.15s",
                          flexShrink:   0,
                        }}
                      >
                        {checkInBusy ? "..." : "Check In"}
                      </button>
                    )
                  }
                />
              );
            }
            return (
              <BriefingRow
                key={i}
                delay={delay}
                Icon={it.Icon}
                iconColor={it.iconColor}
                value={it.value}
                label={it.label}
              />
            );
          })}
        </div>
      )}

      {/* RIGHT — unified to-do rail (chat + project tasks, owner-scoped) */}
      {showItems && (
        <TodoRail
          todos={todos}
          visibleTodos={visibleTodos}
          filter={todoFilter}
          setFilter={setTodoFilter}
          prioritised={prioritised}
          setPrioritised={setPrioritised}
        />
      )}

      {/* CENTER — avatar + brand + greeting */}
      <div
        style={{
          minHeight:      "100vh",
          display:        "flex",
          flexDirection:  "column",
          alignItems:     "center",
          justifyContent: "center",
          textAlign:      "center",
          padding:        "0 24px",
          position:       "relative",
          zIndex:         0,
        }}
      >
        {/* Avatar */}
        <div
          ref={avatarRef}
          style={{
            width:           AVATAR_SIZE,
            height:          AVATAR_SIZE,
            marginBottom:    28,
            animation:       !isFading ? "kiraWelcomeBounce 800ms cubic-bezier(0.34,1.56,0.64,1) backwards" : "none",
            transformOrigin: "center center",
            ...(shrinkStyle || {}),
          }}
        >
          <KiraAvatar emotion={shownEmotion} size={AVATAR_SIZE} />
        </div>

        {/* Brand */}
        <div style={{
          color:      NEON,
          fontSize:   24,
          fontWeight: 600,
          fontFamily: '"Sora", -apple-system, BlinkMacSystemFont, sans-serif',
          letterSpacing: 2,
          marginBottom: 6,
        }}>
          K.I.R.A
        </div>
        <div style={{
          color:      "rgba(255,255,255,0.5)",
          fontSize:   13,
          fontFamily: "inherit",
          marginBottom: 28,
        }}>
          your workplace buddy
        </div>

        {/* Greeting */}
        <div style={{
          color:      "#fff",
          fontSize:   18,
          lineHeight: 1.5,
          textAlign:  "center",
          width:      "100%",
          maxWidth:   520,
          margin:     "0 auto",
          minHeight:  56,
          marginBottom: 24,
        }}>
          {!greeting ? (
            <span className="kira-loading-dots" aria-label="Loading">
              <span>.</span><span>.</span><span>.</span>
            </span>
          ) : (
            <span style={{
              opacity:    typedGreeting ? 1 : 0,
              transition: "opacity 200ms ease",
            }}>
              {typedGreeting}
              {typedGreeting && typedGreeting.length < greeting.length && (
                <span className="kira-caret">|</span>
              )}
            </span>
          )}
        </div>

        {/* Check-in transcript — KIRA's replies render inline here */}
        {showItems && conversation.length > 0 && (
          <div style={{
            width:         "100%",
            maxWidth:      520,
            margin:        "0 auto 14px",
            display:       "flex",
            flexDirection: "column",
            gap:           8,
            maxHeight:     "30vh",
            overflowY:     "auto",
            textAlign:     "left",
            padding:       "4px 2px",
          }}>
            {conversation.map((m, i) => {
              const isUser = m.role === "user";
              return (
                <div key={m.id || i} style={{
                  alignSelf:    isUser ? "flex-end" : "flex-start",
                  maxWidth:     "85%",
                  background:   isUser ? "rgba(0,255,153,0.12)" : "rgba(255,255,255,0.05)",
                  border:       `1px solid ${isUser ? "rgba(0,255,153,0.25)" : "rgba(255,255,255,0.08)"}`,
                  borderRadius: 12,
                  padding:      "8px 12px",
                  color:        isUser ? "#eafff5" : "#e8e8e8",
                  fontSize:     14,
                  lineHeight:   1.45,
                  whiteSpace:   "pre-wrap",
                  wordBreak:    "break-word",
                }}>
                  {m.content}
                </div>
              );
            })}
            {chatLoading && (
              <div style={{
                alignSelf: "flex-start",
                color:     "rgba(255,255,255,0.4)",
                fontSize:  13,
                padding:   "2px 4px",
              }}>
                KIRA is thinking...
              </div>
            )}
            <div ref={transcriptEndRef} />
          </div>
        )}

        {/* Check-in composer — send a message; KIRA replies inline (no navigate) */}
        {showItems && (
          <div style={{
            display:      "flex",
            alignItems:   "center",
            gap:          8,
            width:        "100%",
            maxWidth:     520,
            margin:       "0 auto",
            background:   "rgba(255,255,255,0.04)",
            border:       "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12,
            padding:      "6px 6px 6px 16px",
            animation:    "kiraItemIn 400ms ease backwards",
          }}>
            <input
              ref={composerRef}
              value={composerText}
              onChange={(e) => setComposerText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submitComposer(); }}
              placeholder="Tell KIRA how you're arriving, or ask for anything"
              style={{
                flex:       1,
                background: "transparent",
                border:     "none",
                outline:    "none",
                color:      "#fff",
                fontSize:   14,
              }}
            />
            <button
              onClick={submitComposer}
              disabled={chatLoading}
              aria-label="Send to KIRA"
              style={{
                display:        "inline-flex",
                alignItems:     "center",
                justifyContent: "center",
                width:          38,
                height:         38,
                borderRadius:   9,
                background:     "rgba(0,255,153,0.15)",
                border:         "1px solid rgba(0,255,153,0.4)",
                color:          NEON,
                cursor:         chatLoading ? "default" : "pointer",
                opacity:        chatLoading ? 0.5 : 1,
                flexShrink:     0,
              }}
            >
              <Send size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Skip / dismiss button */}
      {!isFading && (
        <button
          onClick={handleDismiss}
          style={{
            position:    "fixed",
            bottom:      28,
            right:       28,
            background:  "transparent",
            border:      "1px solid rgba(255,255,255,0.15)",
            color:       "rgba(255,255,255,0.6)",
            fontSize:    12,
            padding:     "8px 14px",
            borderRadius:8,
            cursor:      "pointer",
            display:     "flex",
            alignItems:  "center",
            gap:         6,
            transition:  "border-color 0.15s, color 0.15s",
            zIndex:      2,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "rgba(0,255,153,0.4)";
            e.currentTarget.style.color = "#fff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)";
            e.currentTarget.style.color = "rgba(255,255,255,0.6)";
          }}
        >
          Go to dashboard <ArrowRight size={13} />
        </button>
      )}

      <style>{KIRA_WELCOME_CSS}</style>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// BRIEFING ROW (single list item)
// ─────────────────────────────────────────────────────────────

function BriefingRow({ Icon, iconColor, value, label, right, delay }) {
  return (
    <div style={{
      display:       "flex",
      alignItems:    "center",
      gap:           12,
      padding:       "10px 14px",
      background:    "rgba(255,255,255,0.04)",
      border:        "1px solid rgba(255,255,255,0.08)",
      borderRadius:  10,
      animation:     `kiraItemIn 400ms ease ${delay} backwards`,
    }}>
      <Icon size={20} color={iconColor} strokeWidth={2} style={{ flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color:      "#fff",
          fontSize:   14,
          fontWeight: 600,
          overflow:   "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}>
          {value}
        </div>
        <div style={{
          color:    "rgba(255,255,255,0.5)",
          fontSize: 11,
          marginTop: 2,
        }}>
          {label}
        </div>
      </div>
      {right}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TO-DO RAIL (right side) — chat + project tasks, owner-scoped
// ─────────────────────────────────────────────────────────────

const TODO_FILTERS = [
  { key: "all",      label: "All" },
  { key: "due",      label: "Due soon" },
  { key: "priority", label: "Priority" },
  { key: "quick",    label: "Quick wins" },
  { key: "chat",     label: "Chat" },
  { key: "project",  label: "Projects" },
];

function TodoRail({ todos, visibleTodos, filter, setFilter, prioritised, setPrioritised }) {
  const loading = todos == null;
  const total   = Array.isArray(todos) ? todos.length : 0;

  return (
    <div style={{
      position:      "fixed",
      right:         32,
      top:           "50%",
      transform:     "translateY(-50%)",
      width:         320,
      maxHeight:     "78vh",
      display:       "flex",
      flexDirection: "column",
      gap:           12,
      zIndex:        1,
      animation:     "kiraItemIn 400ms ease backwards",
    }}>
      {/* Header + prioritise */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <ListTodo size={18} color={NEON} strokeWidth={2} />
          <span style={{ color: "#fff", fontSize: 14, fontWeight: 600 }}>To-do</span>
          {!loading && (
            <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 12 }}>{total}</span>
          )}
        </div>
        <button
          onClick={() => setPrioritised((p) => !p)}
          title="Order by due date, then priority, then ease"
          style={{
            display:      "inline-flex",
            alignItems:   "center",
            gap:          6,
            background:   prioritised ? "rgba(0,255,153,0.15)" : "transparent",
            border:       `1px solid ${prioritised ? "rgba(0,255,153,0.5)" : "rgba(255,255,255,0.15)"}`,
            color:        prioritised ? NEON : "rgba(255,255,255,0.6)",
            borderRadius: 8,
            padding:      "5px 10px",
            fontSize:     11,
            fontWeight:   600,
            cursor:       "pointer",
          }}
        >
          <Sparkles size={13} /> {prioritised ? "Prioritised" : "Let KIRA prioritise"}
        </button>
      </div>

      {/* Filter chips */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {TODO_FILTERS.map((f) => {
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              style={{
                background:   active ? "rgba(0,255,153,0.15)" : "rgba(255,255,255,0.04)",
                border:       `1px solid ${active ? "rgba(0,255,153,0.45)" : "rgba(255,255,255,0.08)"}`,
                color:        active ? NEON : "rgba(255,255,255,0.6)",
                borderRadius: 20,
                padding:      "4px 11px",
                fontSize:     11,
                cursor:       "pointer",
                whiteSpace:   "nowrap",
              }}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Rows */}
      <div style={{ display: "flex", flexDirection: "column", gap: 8, overflowY: "auto", paddingRight: 2 }}>
        {loading ? (
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, padding: "8px 2px" }}>
            Loading your tasks...
          </div>
        ) : visibleTodos.length === 0 ? (
          <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, padding: "8px 2px" }}>
            {total === 0 ? "You're all clear. Nothing open." : "Nothing matches this filter."}
          </div>
        ) : (
          visibleTodos.map((t, i) => <TodoRow key={t.id || i} todo={t} />)
        )}

        {/* Email — deferred source; static pending slot, no fetch */}
        <div style={{
          display:      "flex",
          alignItems:   "center",
          gap:          10,
          padding:      "10px 12px",
          background:   "rgba(255,255,255,0.02)",
          border:       "1px dashed rgba(255,255,255,0.12)",
          borderRadius: 10,
          opacity:      0.7,
        }}>
          <Mail size={16} color="rgba(255,255,255,0.4)" strokeWidth={2} style={{ flexShrink: 0 }} />
          <div style={{ minWidth: 0 }}>
            <div style={{ color: "rgba(255,255,255,0.65)", fontSize: 13 }}>Email tasks</div>
            <div style={{ color: "rgba(255,255,255,0.35)", fontSize: 11, marginTop: 1 }}>
              Appears once email is connected
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TodoRow({ todo }) {
  const due = dueMeta(todo.due_date);
  const SourceIcon = todo.source === "project" ? Folder : MessageSquare;
  const sourceLabel = todo.source === "project" ? "Project" : "Chat";
  return (
    <div style={{
      display:      "flex",
      alignItems:   "flex-start",
      gap:          10,
      padding:      "10px 12px",
      background:   "rgba(255,255,255,0.04)",
      border:       "1px solid rgba(255,255,255,0.08)",
      borderRadius: 10,
    }}>
      {/* Priority dot */}
      <span style={{
        width:        8,
        height:       8,
        borderRadius: "50%",
        background:   priorityColor(todo.priority),
        marginTop:    5,
        flexShrink:   0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color:        "#fff",
          fontSize:     13,
          lineHeight:   1.35,
          overflow:     "hidden",
          textOverflow: "ellipsis",
          display:      "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
        }}>
          {todo.label}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 5 }}>
          <span style={{
            display:    "inline-flex",
            alignItems: "center",
            gap:        4,
            color:      "rgba(255,255,255,0.45)",
            fontSize:   11,
          }}>
            <SourceIcon size={12} strokeWidth={2} /> {sourceLabel}
          </span>
          {todo.due_date && (
            <span style={{ color: dueColor(due.kind), fontSize: 11, fontWeight: due.kind === "overdue" || due.kind === "today" ? 600 : 400 }}>
              {due.label}
            </span>
          )}
          {todo.ease_hint === "quick" && (
            <span style={{ color: "rgba(0,255,153,0.7)", fontSize: 11 }}>Quick win</span>
          )}
        </div>
      </div>
    </div>
  );
}

// ── To-do arrangement: filter + optional client-side prioritise sort ──
const DUE_RANK  = { overdue: 0, today: 0, soon: 1, future: 2, none: 3 };
const PRIO_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3 };
const EASE_RANK = { quick: 0, moderate: 1, unknown: 1.5, heavy: 2 };

function arrangeTodos(items, filter, prioritised) {
  const out = items.filter((t) => passesFilter(t, filter));
  if (!prioritised) return out;
  // Blend: due urgency → priority → ease (the design's prioritise order).
  return [...out].sort((a, b) => {
    const da = DUE_RANK[dueMeta(a.due_date).kind], db = DUE_RANK[dueMeta(b.due_date).kind];
    if (da !== db) return da - db;
    const pa = PRIO_RANK[a.priority] ?? 2, pb = PRIO_RANK[b.priority] ?? 2;
    if (pa !== pb) return pa - pb;
    const ea = EASE_RANK[a.ease_hint] ?? 1.5, eb = EASE_RANK[b.ease_hint] ?? 1.5;
    return ea - eb;
  });
}

function passesFilter(t, filter) {
  switch (filter) {
    case "due": {
      const k = dueMeta(t.due_date).kind;
      return k === "overdue" || k === "today" || k === "soon";
    }
    case "priority": return t.priority === "Critical" || t.priority === "High";
    case "quick":    return t.ease_hint === "quick";
    case "chat":     return t.source === "chat";
    case "project":  return t.source === "project";
    default:         return true;
  }
}

function dueMeta(due) {
  if (!due) return { label: "No due date", kind: "none" };
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${due}T00:00:00`);
  if (Number.isNaN(d.getTime())) return { label: "No due date", kind: "none" };
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays < 0)   return { label: "Overdue",  kind: "overdue" };
  if (diffDays === 0) return { label: "Today",    kind: "today" };
  if (diffDays === 1) return { label: "Tomorrow", kind: "soon" };
  if (diffDays <= 3)  return { label: `In ${diffDays} days`, kind: "soon" };
  return { label: formatDue(due), kind: "future" };
}

function formatDue(due) {
  try {
    return new Date(`${due}T00:00:00`).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  } catch {
    return due;
  }
}

function priorityColor(p) {
  switch (p) {
    case "Critical": return "#FF5C5C";
    case "High":     return "#FFA23A";
    case "Medium":   return NEON;
    case "Low":      return "rgba(255,255,255,0.35)";
    default:         return "rgba(255,255,255,0.35)";
  }
}

function dueColor(kind) {
  switch (kind) {
    case "overdue": return "#FF5C5C";
    case "today":   return NEON;
    case "soon":    return "#FFC83A";
    default:        return "rgba(255,255,255,0.45)";
  }
}

// ─────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────

function formatElapsed(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

function formatTimeShort(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}

// ─────────────────────────────────────────────────────────────

const KIRA_WELCOME_CSS = `
  @keyframes kiraWelcomeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes kiraWelcomeBounce {
    0%   { transform: scale(0.4); opacity: 0; }
    60%  { transform: scale(1.10); opacity: 1; }
    80%  { transform: scale(0.96); }
    100% { transform: scale(1); opacity: 1; }
  }
  @keyframes kiraItemIn {
    from { opacity: 0; transform: translateX(-20px); }
    to   { opacity: 1; transform: translateX(0); }
  }
  @keyframes kiraCaretBlink {
    0%, 50% { opacity: 1; }
    51%,100% { opacity: 0; }
  }
  .kira-caret {
    display: inline-block;
    margin-left: 2px;
    color: ${NEON};
    font-weight: 300;
    animation: kiraCaretBlink 1s step-end infinite;
  }
  .kira-loading-dots {
    display: inline-flex;
    gap: 4px;
    color: ${NEON};
    font-size: 32px;
    line-height: 0.6;
    letter-spacing: 4px;
  }
  .kira-loading-dots span {
    display: inline-block;
    animation: kiraLoadDotBounce 1.4s ease-in-out infinite;
  }
  .kira-loading-dots span:nth-child(2) { animation-delay: 0.18s; }
  .kira-loading-dots span:nth-child(3) { animation-delay: 0.36s; }
  @keyframes kiraLoadDotBounce {
    0%, 70%, 100% { opacity: 0.25; transform: translateY(0); }
    35%           { opacity: 1;    transform: translateY(-6px); }
  }
`;
